import React, { useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Helmet from 'react-helmet';
import { reduxForm, Field, formValueSelector, getFormValues } from 'redux-form/immutable';
import { LinearProgress, Button, Grid } from '@material-ui/core';
import { DecoratedFormProps } from 'redux-form';

import config from 'config';
import { GMapCircle } from 'utils/map';
import FormTextField from 'retired/FormTextField';
import FormLocationField from 'retired/FormLocationField';
import Box from 'retired/Box';
import PageHeader from 'retired/PageHeader';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { selectUser } from 'entities/user/selectors';
import OrganizationMenu from 'retired/OrganizationMenu';
import { required } from 'utils/validators';
import DialogButton from 'retired/DialogButton';
import FormCheckbox from 'retired/FormCheckbox';

import * as Actions from './actions';
import { FORM_IDENTIFIER } from './constants';
import { selectLoading } from './selectors';
import epics from './epics';
import reducer from './reducer';

const requiredValidator = required();

const key = 'locationFormPage';

const LocationFormPage = ({ submitting, invalid, pristine, change }: DecoratedFormProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const match = useRouteMatch();
  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    dispatch(Actions.initAction(match.params.id));
  }, []);
  const valueSelector = formValueSelector(FORM_IDENTIFIER);
  const state = useSelector((s) => s);
  const name = valueSelector(state, 'name');
  const isPublic = valueSelector(state, 'public');
  const user = useSelector(selectUser);
  const loading = useSelector(selectLoading);

  const currentFormValues = useSelector(getFormValues(FORM_IDENTIFIER));

  const isNew = match.params.id === 'new';

  if (user && !user.isAdmin) {
    history.replace('/');
  }

  const values = currentFormValues?.toJS() || {};
  const showMap = values.latitude && values.longitude;

  const submit = (data = currentFormValues) => {
    dispatch(Actions.saveAction(data.toJS()));
  };

  const handleFormEvent = (event) => {
    event.preventDefault();
    submit();
  };

  const title =
    match.params.id === 'new' ? 'Add New Location' : `${name}${isPublic ? ' (Public)' : ''}`;

  useEffect(() => {
    if (values.restrictToStateCode && !values.restrictToCountryCode) {
      change?.('restrictToCountryCode', true);
    }
  }, [values]);

  return (
    <>
      <div style={{ marginBottom: 60 }}>
        <OrganizationMenu />
      </div>
      <Helmet title={title} />
      <PageHeader
        title={title}
        onArrowClick={() => (history.length > 0 ? history.goBack() : history.push('/locations'))}
      />
      {loading && <LinearProgress />}
      {!loading && (
        <Box>
          {/* eslint-disable-next-line react/no-unknown-property */}
          <form style={{ width: '100%' }} noValidate onSubmitCapture={handleFormEvent}>
            <fieldset>
              <Grid container spacing={3}>
                <Grid item sm={12} lg={12}>
                  <Field
                    id="name"
                    name="name"
                    component={isPublic ? FormTextField : FormLocationField}
                    fullWidth
                    required
                    autoComplete="off"
                    label="Name"
                    helperText="Please enter a full address, once you select a result, the other fields will be auto-populated if they are empty, and be overwritten if not."
                    validate={[requiredValidator]}
                    disabled={isPublic}
                    onSelectPlace={(selectAddress, place) => {
                      selectAddress(place.formattedSuggestion?.mainText, place.placeId);
                      dispatch(Actions.resolveLocationDetails(place));
                    }}
                    sx={{ textTransform: 'uppercase' }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item sm={12} md={6}>
                  <Field
                    id="country"
                    name="country"
                    component={FormTextField}
                    inputProps={{ maxLength: 150 }}
                    fullWidth
                    autoComplete="off"
                    label="Country"
                    helperText="Country"
                    validate={[requiredValidator]}
                    disabled={isPublic}
                  />
                </Grid>
                <Grid item sm={12} md={6}>
                  <Field
                    id="countryCode"
                    name="countryCode"
                    component={FormTextField}
                    inputProps={{ maxLength: 20 }}
                    fullWidth
                    autoComplete="off"
                    label="Country Code"
                    helperText="Country Code"
                    validate={[requiredValidator]}
                    disabled={isPublic}
                  />
                  <Field
                    id="restrictToCountryCode"
                    name="restrictToCountryCode"
                    component={FormCheckbox}
                    label="Only include locations within the radius with this country code"
                    disabled={isPublic}
                  />
                </Grid>
                <Grid item sm={12} md={6}>
                  <Field
                    id="state"
                    name="state"
                    component={FormTextField}
                    inputProps={{ maxLength: 150 }}
                    fullWidth
                    autoComplete="off"
                    label="State"
                    helperText="State"
                    disabled={isPublic}
                  />
                </Grid>
                <Grid item sm={12} md={6}>
                  <Field
                    id="stateCode"
                    name="stateCode"
                    component={FormTextField}
                    inputProps={{ maxLength: 20 }}
                    fullWidth
                    autoComplete="off"
                    label="State Code"
                    helperText="State Code"
                    disabled={isPublic}
                  />
                  <Field
                    id="restrictToStateCode"
                    name="restrictToStateCode"
                    component={FormCheckbox}
                    label="Only include locations within the radius with this state code"
                    disabled={isPublic}
                  />
                </Grid>
                <Grid item sm={12} md={6}>
                  <Field
                    id="currencyCode"
                    name="currencyCode"
                    component={FormTextField}
                    inputProps={{ maxLength: 20 }}
                    fullWidth
                    autoComplete="off"
                    label="Currency Code"
                    helperText="Currency Code"
                    validate={[requiredValidator]}
                    disabled={isPublic}
                  />
                </Grid>
                <Grid item sm={12} md={6}>
                  <Field
                    id="region"
                    name="region"
                    component={FormTextField}
                    inputProps={{ maxLength: 150 }}
                    fullWidth
                    autoComplete="off"
                    label="Region"
                    helperText="Region"
                    disabled={isPublic}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item sm={12} md={6}>
                  <Field
                    id="latitude"
                    name="latitude"
                    component={FormTextField}
                    inputProps={{ maxLength: 150 }}
                    fullWidth
                    autoComplete="off"
                    label="Latitude"
                    helperText="Latitude"
                    validate={[requiredValidator]}
                    disabled={isPublic}
                  />
                </Grid>
                <Grid item sm={12} md={6}>
                  <Field
                    id="longitude"
                    name="longitude"
                    component={FormTextField}
                    inputProps={{ maxLength: 150 }}
                    fullWidth
                    autoComplete="off"
                    label="Longitude"
                    helperText="Longitude"
                    validate={[requiredValidator]}
                    disabled={isPublic}
                  />
                </Grid>
                <Grid item sm={12} md={6}>
                  <Field
                    id="radius"
                    name="radius"
                    component={FormTextField}
                    inputProps={{ maxLength: 150 }}
                    fullWidth
                    autoComplete="off"
                    label="Radius"
                    helperText="Radius in Miles, defaults to 10"
                    validate={[requiredValidator]}
                    disabled={isPublic}
                  />
                </Grid>
              </Grid>

              {showMap && (
                <>
                  <Grid container>
                    <img
                      src={[
                        '//maps.googleapis.com/maps/api/staticmap',
                        `?center=${values.latitude},${values.longitude}`,
                        '&zoom=-1&size=640x360&scale=2',
                        `&markers=color:0x3185FC%7C${values.latitude},${values.longitude}`,
                        `&path=color:0x3185FC|fillcolor:0x3185FC|weight:1${GMapCircle(
                          values.latitude,
                          values.longitude,
                          values.radius * 5280
                        )}`,
                        `&key=${config.googleMapsApiKey}`,
                      ].join('')}
                      alt=""
                      style={{
                        borderRadius: 10,
                        width: '100%',
                      }}
                    />
                  </Grid>
                </>
              )}

              <div style={{ marginTop: 30 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting || invalid || pristine || isPublic}
                >
                  {match.params.id === 'new' ? 'Submit' : 'Save'}
                </Button>

                {!isNew && !isPublic && (
                  <DialogButton
                    style={{ display: 'inline-block', marginLeft: 30 }}
                    buttonLabel="Remove Location"
                    title="Remove the location"
                    text="Are you sure you want to remove this location? This action is irreversible"
                    confirmLabel="Remove"
                    cancelLabel="Go Back"
                    handleConfirm={() => {
                      dispatch(Actions.removeAction(match.params.id));
                    }}
                  />
                )}
              </div>
            </fieldset>
          </form>
        </Box>
      )}
    </>
  );
};

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default withForm(LocationFormPage);
